import { useState, useRef, useEffect, useCallback } from 'react';

const T = {
  bg:'#06060d',s1:'#0d0c18',s2:'#12111e',s3:'#181628',s4:'#1e1c30',
  border:'rgba(255,255,255,0.07)',border2:'rgba(255,255,255,0.13)',
  gold:'#d4a853',gold2:'#f0c878',cream:'#f0ead8',text2:'#9890b0',
  text3:'#55506a',teal:'#1a8a76',purple:'#8b5cf6',error:'#ff6b6b',
};

const SPARKY_SYSTEM = `You are Sparky, a creative catalyst built into Artizan, a professional network for multi-hyphenate creative artists. Your entire purpose is to get artists thinking deeper, wider, and more honestly about their work. You are NOT a decision-maker. You are a spark.

CORE RULES:
1. NEVER tell an artist what to do or which direction to take.
2. NEVER rank their options or suggest one path is better.
3. ALWAYS respond with questions and perspectives that open up thinking.
4. Keep responses short. 2-4 sentences max, then 1-2 questions.
5. Be warm, real, and direct. No corporate speak.
6. If they ask you to decide for them, reflect it back: what does their gut say?

YOU COVER: creative brainstorming, career direction, craft feedback, business questions, and anything an artist might ever need to think through.
TOUR DETECTION: If the user asks what they should try next, what features exist, how to use a feature, or says something like "what should I explore", "show me around", "what can I do", "give me a tour", "what haven't I tried" — respond with EXACTLY this JSON on its own line: {"action":"tour","intent":"detected"} — then add a short warm sentence. Nothing else.

TONE: You sound like a creative friend who knows a lot. Conversational. Real. Always in their corner. Never preachy.`;

const STARTERS = [
  { icon:'🗺️', label:"Take me on a platform tour", prompt:"__TOUR__", isTour:true },
  { icon:'🔥', label:"I'm stuck on a project", prompt:"I'm creatively stuck and need to get unstuck." },
  { icon:'🎯', label:"What should I pursue next?", prompt:"I'm trying to figure out my next direction." },
  { icon:'✦',  label:"Feedback on my work", prompt:"I want honest feedback on something I'm working on." },
  { icon:'💰', label:"Business & money questions", prompt:"I have questions about rates, contracts, or pitching." },
  { icon:'⚡', label:"Just spark something", prompt:"I don't have a specific question — just spark something in me." },
];

export default function Sparky({ onStartTour }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showStarters, setShowStarters] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── DRAG STATE ──
  const [pos, setPos] = useState({ x: -999, y: -999 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const btnRef = useRef(null);

  // Set default position on mount
  useEffect(() => {
    const calcPos = () => {
      const btnSize = 50;
      const isMobile = window.innerWidth < 768;
      const x = window.innerWidth - btnSize - 20;
      const y = window.innerHeight - btnSize - (isMobile ? 140 : 90);
      setPos(prev => {
        // Only reset if off screen
        if (prev.x === -999 || prev.x > window.innerWidth - 20 || prev.y > window.innerHeight - 20) {
          return { x: Math.max(10, x), y: Math.max(10, y) };
        }
        // Keep current position if still on screen
        return { x: Math.min(prev.x, window.innerWidth - 58), y: Math.min(prev.y, window.innerHeight - 68) };
      });
    };
    calcPos();
    window.addEventListener('resize', calcPos);
    return () => window.removeEventListener('resize', calcPos);
  }, []);

  // ── DRAG HANDLERS ──
  const onPointerDown = useCallback((e) => {
    if (e.target.closest('button') && e.target !== btnRef.current && !btnRef.current?.contains(e.target)) return;
    dragStart.current = {
      startX: e.clientX - pos.x,
      startY: e.clientY - pos.y,
      moved: false,
    };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    if (!dragStart.current) return;
    const dx = Math.abs(e.clientX - (dragStart.current.startX + pos.x));
    const dy = Math.abs(e.clientY - (dragStart.current.startY + pos.y));
    if (dx > 5 || dy > 5) dragStart.current.moved = true;
    const newX = Math.max(8, Math.min(window.innerWidth - 58, e.clientX - dragStart.current.startX));
    const newY = Math.max(8, Math.min(window.innerHeight - 68, e.clientY - dragStart.current.startY));
    setPos({ x: newX, y: newY });
  }, [pos]);

  const onPointerUp = useCallback((e) => {
    if (dragStart.current && !dragStart.current.moved) {
      setOpen(o => !o);
    }
    dragStart.current = null;
    setDragging(false);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior:'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open && !showStarters) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, showStarters]);

  const TOUR_INTENTS = ['what should i try','what should i work','what can i do','show me around','give me a tour','what have i not tried','what else is there','what features','how do i use','walk me through','what should i explore','next feature','tour','show me what','take me through','what is there','what do i do next','where should i go','what next','show me everything','explore','guide me'];

  const detectTourIntent = (text) => TOUR_INTENTS.some(p => text.toLowerCase().includes(p));

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    if (detectTourIntent(text) && onStartTour) {
      setMessages(prev => [...prev, { role: 'user', content: text }]);
      setShowStarters(false);
      setInput('');
      setTimeout(() => {
        setMessages(prev => [...prev, { role: 'assistant', content: "On it — walking you through your next unexplored feature. ⚡" }]);
        setTimeout(() => onStartTour(), 700);
      }, 300);
      return;
    }
    if (!text.trim() || loading) return;
    const userMessage = { role:'user', content:text.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setError(null);
    setShowStarters(false);
    try {
      const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
      if (!apiKey) throw new Error('NO_KEY');
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'x-api-key':apiKey,
          'anthropic-version':'2023-06-01',
          'anthropic-dangerous-direct-browser-access':'true',
        },
        body:JSON.stringify({
          model:'claude-sonnet-4-20250514',
          max_tokens:400,
          system:SPARKY_SYSTEM,
          messages:newMessages,
        }),
      });
      if (!response.ok) { const e = await response.json().catch(()=>({})); throw new Error(e.error?.message||`Error ${response.status}`); }
      const data = await response.json();
      setMessages(prev => [...prev, { role:'assistant', content:data.content[0]?.text||'' }]);
    } catch(err) {
      setError(err.message === 'NO_KEY' ? 'no_key' : err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setMessages([]); setInput(''); setError(null); setShowStarters(true); };

  // Panel position — opens upward from button
  const panelWidth = window.innerWidth < 768 ? window.innerWidth - 16 : 380;
  const panelLeft = Math.max(8, Math.min(pos.x, window.innerWidth - panelWidth - 8));
  const panelBottom = Math.max(8, window.innerHeight - pos.y + 8);

  if (pos.x === -999) return null;

  return (
    <>
      {/* ── DRAGGABLE BUTTON ── */}
      <div
        ref={btnRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position:'fixed',
          left: pos.x,
          top: pos.y,
          width:50,height:50,
          zIndex:1000,
          touchAction:'none',
          userSelect:'none',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        <button
          style={{
            width:'100%',height:'100%',borderRadius:'50%',
            background:open?T.s3:`linear-gradient(135deg,${T.gold},${T.gold2})`,
            border:open?`2px solid ${T.border2}`:'none',
            display:'flex',alignItems:'center',justifyContent:'center',
            fontSize:open?18:22,
            boxShadow:open?'none':`0 4px 20px rgba(212,168,83,0.4)`,
            color:open?T.cream:T.bg,
            cursor: dragging ? 'grabbing' : 'pointer',
            pointerEvents:'none',
          }}
        >
          {open?'✕':'⚡'}
        </button>
        {!open && (
          <div style={{
            position:'absolute',top:-6,right:-6,
            background:T.teal,color:'white',
            borderRadius:'50%',width:16,height:16,
            fontSize:9,fontWeight:700,
            display:'flex',alignItems:'center',justifyContent:'center',
            pointerEvents:'none',
          }}>AI</div>
        )}
      </div>

      {/* ── DRAG HINT ── */}
      {!open && !dragging && messages.length === 0 && (
        <div style={{
          position:'fixed',
          left: pos.x - 60,
          top: pos.y + 54,
          fontSize:9,color:T.text3,
          pointerEvents:'none',
          whiteSpace:'nowrap',
          fontFamily:'inherit',
        }}>
          drag me anywhere
        </div>
      )}

      {/* ── CHAT PANEL ── */}
      {open && (
        <div style={{
          position:'fixed',
          left: panelLeft,
          bottom: panelBottom,
          width: panelWidth,
          maxHeight:'60vh',
          background:T.s1,border:`1px solid ${T.border2}`,borderRadius:16,
          display:'flex',flexDirection:'column',
          boxShadow:'0 20px 60px rgba(0,0,0,0.6)',
          zIndex:999,overflow:'hidden',
          fontFamily:"'DM Sans','Jost',sans-serif",
        }}>
          {/* Header */}
          <div style={{padding:'0.9rem 1.1rem',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',background:T.s2}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:30,height:30,borderRadius:'50%',background:`linear-gradient(135deg,${T.gold},${T.purple})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>⚡</div>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:T.cream}}>Sparky</div>
                <div style={{fontSize:10,color:T.text3}}>Creative catalyst · Claude</div>
              </div>
            </div>
            {messages.length > 0 && <button onClick={reset} style={{background:'none',border:`1px solid ${T.border2}`,borderRadius:6,padding:'3px 8px',fontSize:10,color:T.text2,cursor:'pointer',fontFamily:'inherit'}}>New</button>}
          </div>

          {/* Messages */}
          <div style={{flex:1,overflowY:'auto',padding:'0.85rem',display:'flex',flexDirection:'column',gap:10,minHeight:160,maxHeight:360}}>
            {showStarters && messages.length === 0 && (
              <div>
                <div style={{fontSize:13,color:T.text2,marginBottom:12,lineHeight:1.6}}>Hey. What's on your mind?</div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {STARTERS.map((s,i) => (
                    <button key={i} onClick={()=>{ if(s.isTour && onStartTour){ setMessages(prev=>[...prev,{role:'user',content:'Take me on a platform tour'},{role:'assistant',content:"On it — walking you through every feature you haven't explored yet. ⚡"}]); setShowStarters(false); setTimeout(()=>onStartTour(),700); } else sendMessage(s.prompt); }} style={{background:s.isTour?`linear-gradient(135deg,${T.gold}22,${T.purple}22)`:T.s3,border:`1px solid ${s.isTour?T.gold:T.border2}`,borderRadius:10,padding:'0.55rem 0.85rem',textAlign:'left',cursor:'pointer',fontSize:12,color:s.isTour?T.gold:T.cream,fontFamily:'inherit',display:'flex',alignItems:'center',gap:8,fontWeight:s.isTour?700:400}}>
                      <span style={{fontSize:14}}>{s.icon}</span>{s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg,i) => (
              <div key={i} style={{display:'flex',justifyContent:msg.role==='user'?'flex-end':'flex-start'}}>
                {msg.role==='assistant' && <div style={{width:22,height:22,borderRadius:'50%',background:`linear-gradient(135deg,${T.gold},${T.purple})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,flexShrink:0,marginRight:6,marginTop:2}}>⚡</div>}
                <div style={{maxWidth:'78%',padding:'0.55rem 0.85rem',borderRadius:msg.role==='user'?'16px 16px 4px 16px':'16px 16px 16px 4px',background:msg.role==='user'?`linear-gradient(135deg,${T.gold},${T.gold2})`:T.s3,color:msg.role==='user'?T.bg:T.cream,fontSize:12,lineHeight:1.6,whiteSpace:'pre-wrap'}}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div style={{width:22,height:22,borderRadius:'50%',background:`linear-gradient(135deg,${T.gold},${T.purple})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10}}>⚡</div>
                <div style={{background:T.s3,borderRadius:'16px 16px 16px 4px',padding:'0.55rem 0.85rem',display:'flex',gap:4,alignItems:'center'}}>
                  {[0,1,2].map(i=><div key={i} style={{width:6,height:6,borderRadius:'50%',background:T.gold,animation:`sparkPulse 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}
                </div>
              </div>
            )}
            {error==='no_key' && (
              <div style={{background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:10,padding:'0.75rem',fontSize:11,color:T.cream,lineHeight:1.7}}>
                <div style={{fontWeight:700,marginBottom:4,color:T.gold}}>⚡ Need API key</div>
                <div style={{color:T.text2}}>1. Go to console.anthropic.com<br/>2. Create an API key<br/>3. Add to your .env file</div>
              </div>
            )}
            {error && error!=='no_key' && (
              <div style={{background:'rgba(255,107,107,0.1)',border:'1px solid rgba(255,107,107,0.3)',borderRadius:10,padding:'0.65rem',fontSize:11,color:T.error}}>
                {error}. Try again.
              </div>
            )}
            <div ref={messagesEndRef}/>
          </div>

          {/* Input */}
          <div style={{padding:'0.65rem',borderTop:`1px solid ${T.border}`,display:'flex',gap:7,background:T.s2}}>
            <textarea ref={inputRef} value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage(input);}}} placeholder="What's on your mind..." rows={1} disabled={loading}
              style={{flex:1,background:T.s3,border:`1px solid ${T.border2}`,borderRadius:9,padding:'0.55rem 0.75rem',color:T.cream,fontSize:12,fontFamily:'inherit',resize:'none',outline:'none',lineHeight:1.5,maxHeight:80,overflowY:'auto',opacity:loading?0.5:1}}
              onInput={e=>{e.target.style.height='auto';e.target.style.height=Math.min(e.target.scrollHeight,80)+'px';}}
            />
            <button onClick={()=>sendMessage(input)} disabled={!input.trim()||loading} style={{width:34,height:34,borderRadius:9,background:input.trim()&&!loading?`linear-gradient(135deg,${T.gold},${T.gold2})`:T.s3,border:'none',cursor:input.trim()&&!loading?'pointer':'default',fontSize:14,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:input.trim()&&!loading?T.bg:T.text3}}>➤</button>
          </div>
          <div style={{padding:'5px 10px',fontSize:9,color:T.text3,textAlign:'center',borderTop:`1px solid ${T.border}`,background:T.s2}}>
            Sparky sparks thinking — never steers.
          </div>
        </div>
      )}
      <style>{`@keyframes sparkPulse{0%,80%,100%{transform:scale(0.7);opacity:0.4}40%{transform:scale(1);opacity:1}}`}</style>
    </>
  );
}
